'use client'

import type { ReactNode } from 'react'
import { proxy, useSnapshot } from 'valtio'
import { ConfirmDialog } from './confirm-dialog'

interface ConfirmDialogCommandOptions {
  cancelText?: string
  confirmText?: string
  confirmTone?: 'primary' | 'danger'
  description: ReactNode
  title: ReactNode
}

interface ConfirmDialogCommandRequest extends ConfirmDialogCommandOptions {
  id: number
  resolve: (confirmed: boolean) => void
}

const confirmDialogCommandStore = proxy<{
  current: ConfirmDialogCommandRequest | null
}>({
  current: null,
})

let nextConfirmDialogCommandId = 1

function finishConfirmDialogCommand(id: number, confirmed: boolean) {
  const current = confirmDialogCommandStore.current
  if (!current || current.id !== id) {
    return
  }

  confirmDialogCommandStore.current = null
  current.resolve(confirmed)
}

export function requestConfirmDialog(options: ConfirmDialogCommandOptions) {
  return new Promise<boolean>((resolve) => {
    confirmDialogCommandStore.current?.resolve(false)
    confirmDialogCommandStore.current = {
      ...options,
      id: nextConfirmDialogCommandId,
      resolve,
    }
    nextConfirmDialogCommandId += 1
  })
}

export function ConfirmDialogCommandHost() {
  useSnapshot(confirmDialogCommandStore)
  const current = confirmDialogCommandStore.current

  if (!current) {
    return null
  }

  return (
    <ConfirmDialog
      cancelText={current.cancelText}
      confirmText={current.confirmText}
      confirmTone={current.confirmTone}
      description={current.description}
      onConfirm={() => {
        finishConfirmDialogCommand(current.id, true)
      }}
      onOpenChange={(open) => {
        if (!open) {
          finishConfirmDialogCommand(current.id, false)
        }
      }}
      open
      title={current.title}
    />
  )
}
